import { supabase } from '@/lib/supabaseClient';
import { PigTransferRecord } from '@/types/database';

export interface CreateTransferPayload {
  transfer_date: string;
  source_cycle_id?: string | null;
  destination_cycle_id?: string | null;
  source_unit_id?: string | null;
  destination_unit_id?: string | null;
  head_count: number;
  total_weight_kg?: number | null;
  reason: string;
  notes?: string | null;
}

export const transferService = {
  /**
   * Fetches all transfers associated with a cycle (either as source or destination).
   */
  async getTransfersByCycle(cycleId: string): Promise<{
    transfersIn: PigTransferRecord[];
    transfersOut: PigTransferRecord[];
  }> {
    const [inRes, outRes] = await Promise.all([
      supabase
        .from('pig_transfers')
        .select(`
          *,
          source_cycle:production_cycles!pig_transfers_source_cycle_id_fkey (id, cycle_code),
          destination_cycle:production_cycles!pig_transfers_destination_cycle_id_fkey (id, cycle_code),
          source_unit:production_units!pig_transfers_source_unit_id_fkey (id, unit_code, unit_name),
          destination_unit:production_units!pig_transfers_destination_unit_id_fkey (id, unit_code, unit_name)
        `)
        .eq('destination_cycle_id', cycleId)
        .eq('is_void', false)
        .order('transfer_date', { ascending: false }),

      supabase
        .from('pig_transfers')
        .select(`
          *,
          source_cycle:production_cycles!pig_transfers_source_cycle_id_fkey (id, cycle_code),
          destination_cycle:production_cycles!pig_transfers_destination_cycle_id_fkey (id, cycle_code),
          source_unit:production_units!pig_transfers_source_unit_id_fkey (id, unit_code, unit_name),
          destination_unit:production_units!pig_transfers_destination_unit_id_fkey (id, unit_code, unit_name)
        `)
        .eq('source_cycle_id', cycleId)
        .eq('is_void', false)
        .order('transfer_date', { ascending: false }),
    ]);

    if (inRes.error) {
      console.error('[transferService.getTransfersByCycle] inRes error:', inRes.error.message);
    }
    if (outRes.error) {
      console.error('[transferService.getTransfersByCycle] outRes error:', outRes.error.message);
    }

    return {
      transfersIn: (inRes.data || []) as PigTransferRecord[],
      transfersOut: (outRes.data || []) as PigTransferRecord[],
    };
  },

  /**
   * Creates a new pig transfer record.
   */
  async createTransfer(payload: CreateTransferPayload): Promise<PigTransferRecord> {
    if (!payload.transfer_date) {
      throw new Error('Transfer date is required.');
    }
    if (!payload.head_count || payload.head_count <= 0) {
      throw new Error('Transfer head count must be greater than zero.');
    }
    if (!payload.reason || !payload.reason.trim()) {
      throw new Error('Transfer reason is required.');
    }
    if (!payload.source_cycle_id && !payload.destination_cycle_id) {
      throw new Error('At least one of source cycle or destination cycle must be specified.');
    }
    if (
      payload.source_cycle_id &&
      payload.destination_cycle_id &&
      payload.source_cycle_id === payload.destination_cycle_id &&
      payload.source_unit_id === payload.destination_unit_id
    ) {
      throw new Error('Source and destination cannot be identical.');
    }

    const insertData: any = {
      transfer_date: payload.transfer_date,
      source_cycle_id: payload.source_cycle_id || null,
      destination_cycle_id: payload.destination_cycle_id || null,
      source_unit_id: payload.source_unit_id || null,
      destination_unit_id: payload.destination_unit_id || null,
      head_count: payload.head_count,
      total_weight_kg: payload.total_weight_kg ? Number(payload.total_weight_kg) : null,
      reason: payload.reason.trim(),
      notes: payload.notes?.trim() || null,
    };

    const { data, error } = await supabase
      .from('pig_transfers')
      .insert(insertData)
      .select('*')
      .single();

    if (error || !data) {
      console.error('[transferService.createTransfer] Insert error:', error?.message);
      throw new Error(`Failed to create pig transfer: ${error?.message}`);
    }

    return data as PigTransferRecord;
  },
};
